import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { useLocation } from 'react-router-dom';
import Loader from '../components/Loader';

const LoadingContext = createContext({
  isLoading: false,
  showLoading: () => {},
  hideLoading: () => {}
});

export function useLoading() {
  return useContext(LoadingContext);
}

const MIN_INITIAL_LOAD_MS = 600;
const ROUTE_LOAD_MS = 450;

function removeInitialLoader() {
  const initialLoader = document.getElementById('initial-loader');
  if (initialLoader) {
    initialLoader.remove();
  }
}

export function LoadingProvider({ children }) {
  const location = useLocation();
  const [initialLoading, setInitialLoading] = useState(true);
  const [routeLoading, setRouteLoading] = useState(false);
  const [manualLoading, setManualLoading] = useState(false);
  const manualCountRef = useRef(0);
  const isFirstRouteRef = useRef(true);
  const mountedAtRef = useRef(Date.now());

  useEffect(() => {
    removeInitialLoader();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const finishInitialLoad = () => {
      const elapsed = Date.now() - mountedAtRef.current;
      const remaining = Math.max(0, MIN_INITIAL_LOAD_MS - elapsed);

      window.setTimeout(() => {
        if (!cancelled) {
          setInitialLoading(false);
        }
      }, remaining);
    };

    if (document.readyState === 'complete') {
      finishInitialLoad();
    } else {
      window.addEventListener('load', finishInitialLoad);
    }

    return () => {
      cancelled = true;
      window.removeEventListener('load', finishInitialLoad);
    };
  }, []);

  useEffect(() => {
    if (isFirstRouteRef.current) {
      isFirstRouteRef.current = false;
      return undefined;
    }

    setRouteLoading(true);
    window.scrollTo({ top: 0, behavior: 'auto' });

    const timer = window.setTimeout(() => {
      setRouteLoading(false);
    }, ROUTE_LOAD_MS);

    return () => window.clearTimeout(timer);
  }, [location.pathname]);

  const showLoading = useCallback(() => {
    manualCountRef.current += 1;
    setManualLoading(true);
  }, []);

  const hideLoading = useCallback(() => {
    manualCountRef.current = Math.max(0, manualCountRef.current - 1);

    if (manualCountRef.current === 0) {
      setManualLoading(false);
    }
  }, []);

  const isLoading = initialLoading || routeLoading || manualLoading;

  return (
    <LoadingContext.Provider value={{ isLoading, showLoading, hideLoading }}>
      {isLoading && <Loader label={initialLoading ? 'Loading Buni Hub...' : 'Loading page...'} />}
      <div className={`app-shell${isLoading ? ' app-shell--loading' : ''}`} aria-busy={isLoading}>
        {children}
      </div>
    </LoadingContext.Provider>
  );
}
